import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scale } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { countries, getCountry } from "@/data/countries";
import { compareMeta } from "@/data/compare";

const Select = ({ value, onChange, exclude, testid, label }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal block mb-2">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none bg-white border border-pine/15 rounded-2xl px-5 py-4 font-serif font-semibold text-xl text-pine focus:outline-none focus:ring-2 focus:ring-petal/40 cursor-pointer"
    >
      {countries.filter((c) => !exclude.includes(c.slug)).map((c) => (
        <option key={c.slug} value={c.slug}>{c.name}</option>
      ))}
    </select>
  </div>
);

const Cell = ({ children, win }) => (
  <div className={`px-5 py-4 text-sm leading-relaxed ${win ? "bg-blush font-semibold text-pine" : "text-ink/80"}`}>
    {children}
  </div>
);

const BarCell = ({ value, max, win }) => {
  const m = max ?? (value > 5 ? 100 : 5);
  return (
    <div className={`px-5 py-4 ${win ? "bg-blush" : ""}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-pine/10 overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${win ? "bg-petal" : "bg-pine/40"}`}
            initial={{ width: 0 }}
            animate={{ width: `${(value / m) * 100}%` }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <span className="font-mono text-xs text-pine shrink-0">{value}{m > 5 ? "%" : "/5"}</span>
      </div>
    </div>
  );
};

const GRID = "grid-cols-[1.1fr_1fr_1fr_1fr]";

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [picks, setPicks] = useState(() => {
    const fromUrl = ["a", "b", "c"].map((k) => searchParams.get(k)).filter((s) => s && getCountry(s));
    return fromUrl.length === 3 ? fromUrl : ["malaysia", "egypt", "uae"];
  });
  const [copied, setCopied] = useState(false);
  const setPick = (i, slug) => setPicks((p) => p.map((x, j) => (j === i ? slug : x)));
  const triples = picks.map((slug) => ({ slug, c: getCountry(slug), m: compareMeta[slug] }));

  useEffect(() => {
    setSearchParams({ a: picks[0], b: picks[1], c: picks[2] }, { replace: true });
  }, [picks, setSearchParams]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      window.prompt("Copy this link:", window.location.href);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const rows = [
    { label: "Safety for women & children", get: (c) => c.mom.scores.safety, better: "high", kind: "bar" },
    { label: "Muslim community", get: (c) => c.mom.scores.community, better: "high", kind: "bar" },
    { label: "Affordability (Mom's score)", get: (c) => c.mom.scores.cost, better: "high", kind: "bar" },
    { label: "Easy bureaucracy", get: (c) => c.mom.scores.bureaucracy, better: "high", kind: "bar" },
    { label: "Healthcare vs US standard", get: (c, m) => m.hc, better: "high", kind: "bar" },
    { label: "Visa ease for US citizens", get: (c, m) => m.visaEase, better: "high", kind: "bar" },
    { label: "Family monthly budget", get: (c, m) => (m.costLow + m.costHigh) / 2, better: "low", kind: "text", text: (c, m) => `$${m.costLow.toLocaleString()} – $${m.costHigh.toLocaleString()} / mo` },
    { label: "Rent (family apartment)", kind: "text", text: (c) => c.costOfLiving.rent },
    { label: "Climate", kind: "text", text: (c) => c.quickFacts.climate },
    { label: "Flight from NYC", kind: "text", text: (c) => c.quickFacts.flightTime },
    { label: "Language reality", kind: "text", text: (c) => c.quickFacts.language },
    { label: "Visa snapshot", kind: "text", text: (c, m) => m.visaNote },
  ];

  const winners = (row) => {
    if (!row.better) return triples.map(() => false);
    const vals = triples.map(({ c, m }) => row.get(c, m));
    if (new Set(vals).size < 2) return vals.map(() => false);
    const best = row.better === "high" ? Math.max(...vals) : Math.min(...vals);
    return vals.map((v) => v === best);
  };

  return (
    <div data-testid="compare-page" className="pt-24 lg:pt-28 bg-cream min-h-screen grain relative">
      <Seo
        title="Compare Three Hijrah Destinations Side-by-Side — Hijrah Mom"
        siteName="Hijrah Mom"
        description="Compare three Muslim-majority countries side-by-side on cost of living, visa ease for US citizens, healthcare, safety, community, and more."
      />
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><Eyebrow>The honest scales</Eyebrow></div>
          <h1 className="mt-4 font-serif font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Three countries. <span className="italic text-petal">One decision.</span>
          </h1>
          <p className="mt-5 text-sage leading-relaxed">
            Pick three destinations and weigh them on what matters: cost, visa ease, safety, community, and my honest scores. Pink means it wins that row — ties share the crown.
          </p>
        </div>

        <FadeUp className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 items-end max-w-4xl mx-auto">
          {picks.map((slug, i) => (
            <Select
              key={i}
              value={slug}
              onChange={(v) => setPick(i, v)}
              exclude={picks.filter((_, j) => j !== i)}
              testid={`compare-select-${["a", "b", "c"][i]}`}
              label={`Country ${["A", "B", "C"][i]}`}
            />
          ))}
        </FadeUp>

        <FadeUp className="mt-6 flex justify-center">
          <button
            data-testid="compare-share-btn"
            onClick={copyLink}
            className="inline-flex items-center gap-2 border border-pine/25 text-pine text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-blush-deep hover:border-petal/40 transition-colors"
          >
            {copied ? "Link copied — share it with a sister!" : "Copy link to this comparison"}
          </button>
        </FadeUp>

        <FadeUp className="mt-10 bg-white rounded-3xl border border-pine/10 shadow-sm overflow-hidden" data-testid="compare-table">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className={`grid ${GRID} items-stretch border-b border-pine/10`}>
                <div className="px-5 py-5 flex items-end"><Scale size={18} className="text-pine/40" /></div>
                {triples.map(({ c }) => (
                  <div key={c.slug} className="px-5 py-5 border-l border-pine/10 flex items-center gap-3">
                    <img src={c.image} alt={c.imageAlt} className="w-12 h-12 rounded-xl object-cover hidden sm:block" loading="lazy" />
                    <div>
                      <p className="font-serif font-semibold text-lg text-pine leading-tight">{c.name}</p>
                      <p className="font-mono text-[10px] tracking-widest uppercase font-semibold text-pine-dark">{c.region}</p>
                    </div>
                  </div>
                ))}
              </div>
              {rows.map((row, i) => {
                const wins = winners(row);
                return (
                  <div key={row.label} className={`grid ${GRID} ${i % 2 ? "bg-blush/40" : ""}`} data-testid={`compare-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="px-5 py-4 font-mono text-[10px] sm:text-xs tracking-widest uppercase text-sage flex items-center">{row.label}</div>
                    {triples.map(({ c, m }, j) => (
                      <div key={c.slug} className="border-l border-pine/10">
                        {row.kind === "bar" ? (
                          <BarCell value={row.get(c, m)} win={wins[j]} />
                        ) : (
                          <Cell win={wins[j]}>{row.text(c, m)}</Cell>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className={`grid ${GRID} border-t border-pine/10 bg-blush/60`}>
                <div className="px-5 py-5" />
                {triples.map(({ c }) => (
                  <div key={c.slug} className="px-5 py-5 border-l border-pine/10">
                    <Link
                      to={`/countries/${c.slug}`}
                      data-testid={`compare-profile-link-${c.slug}`}
                      className="inline-flex items-center gap-2 bg-pine text-cream text-xs font-semibold px-4 py-2.5 rounded-full hover:bg-petal transition-colors"
                    >
                      Full profile <ArrowRight size={13} />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>

        <p className="mt-6 text-center text-xs text-sage-muted max-w-xl mx-auto leading-relaxed">
          Scores and budgets are one mother's honest estimates for a family of four — a starting point for your own research, not the final word.
        </p>
      </section>
    </div>
  );
}
